import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { message } from 'antd'
import { useToken } from 'app/hooks'
import API from 'utils/api'

type TemplateStatus = 'Y' | 'I' | 'R'

interface KakaoTemplate {
	templateCode: string
	templateName: string
	templateContent: string
	status: TemplateStatus
	templateStatusMsg: string
	templateStatusComments?: string
	createDate: string
}

interface KakaoTemplateListResult {
	code?: number
	message?: string
	totalCount?: number
	list?: KakaoTemplate[]
}

interface KakaoTemplateCatalogProps {
	refreshKey?: number
}

const KakaoTemplateCatalog: React.FC<KakaoTemplateCatalogProps> = ({ refreshKey = 0 }) => {
	const { data: token } = useToken()

	const [loading, setLoading] = useState(false)
	const [templates, setTemplates] = useState<KakaoTemplate[]>([])
	const [filters, setFilters] = useState<{
		templateStatus: TemplateStatus | '' // 기본: 사용중(Y)
		templateCode: string
		templateName: string
		pageNum: number
		pageSize: number
	}>({
		templateStatus: 'Y',
		templateCode: '',
		templateName: '',
		pageNum: 1,
		pageSize: 20,
	})

	const [pagination, setPagination] = useState<{
		totalCount: number
		maxPage: number
	}>({
		totalCount: 0,
		maxPage: 1,
	})

	const fetchTemplates = async () => {
		if (!token) return
		setLoading(true)
		try {
			const params = new URLSearchParams()
			if (filters.templateStatus) params.append('templateStatus', filters.templateStatus)
			if (filters.templateCode.trim()) params.append('templateCode', filters.templateCode.trim())
			if (filters.templateName.trim()) params.append('templateName', filters.templateName.trim())
			if (filters.pageNum) params.append('pageNum', String(filters.pageNum))
			if (filters.pageSize) params.append('pageSize', String(filters.pageSize))

			const res: any = await API.get(`/message/templates?${params.toString()}`, token + '', {})

			if (res?.code !== 0) {
				message.error(res?.message || '템플릿 목록 조회에 실패했습니다.')
				setTemplates([])
				setPagination({ totalCount: 0, maxPage: 1 })
				return
			}

			const result: KakaoTemplateListResult = res?.result || {}
			const list = Array.isArray(result.list) ? result.list : []
			const totalCount = typeof result.totalCount === 'number' ? result.totalCount : list.length
			const maxPage =
				filters.pageSize > 0 ? Math.max(1, Math.ceil(totalCount / filters.pageSize)) : 1

			setTemplates(list)
			setPagination({ totalCount, maxPage })
		} catch (err: any) {
			console.error(err)
			message.error('템플릿 목록 조회 중 오류가 발생했습니다.')
			setTemplates([])
			setPagination({ totalCount: 0, maxPage: 1 })
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		if (token) {
			fetchTemplates()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token, filters.pageNum, filters.pageSize, filters.templateStatus, refreshKey])

	const handleFilterChange = (key: 'templateStatus' | 'templateCode' | 'templateName', value: string) => {
		setFilters(prev => ({
			...prev,
			[key]: value,
			pageNum: 1,
		}))
	}

	const handleResetFilters = () => {
		setFilters(prev => ({
			...prev,
			templateStatus: 'Y',
			templateCode: '',
			templateName: '',
			pageNum: 1,
		}))
	}

	const handlePageChange = (page: number) => {
		setFilters(prev => ({
			...prev,
			pageNum: Math.min(Math.max(page, 1), pagination.maxPage || 1),
		}))
	}

	const handleSetDefaultTemplate = async (
		template: KakaoTemplate,
		type: 'absence' | 'report' | 'attendance'
	) => {
		if (!token) return
		const confirmMessage =
			type === 'absence'
				? '이 템플릿을 결시 알림 기본 템플릿으로 설정하시겠습니까?'
				: type === 'report'
				? '이 템플릿을 일지발송 기본 템플릿으로 설정하시겠습니까?'
				: '이 템플릿을 등원 알림 기본 템플릿으로 설정하시겠습니까?'

		const ok = window.confirm(confirmMessage)
		if (!ok) return

		try {
			const res: any = await API.put(
				'/message/templates/defaults',
				token + '',
				{
					type,
					templateCode: template.templateCode,
					templateName: template.templateName,
					description: template.templateStatusMsg || '',
				}
			)

			if (res?.code === 0 && res?.result?.success) {
				message.success('기본 템플릿이 설정되었습니다.')
			} else {
				message.error(res?.message || '기본 템플릿 설정에 실패했습니다.')
			}
		} catch (err: any) {
			console.error(err)
			message.error('기본 템플릿 설정 중 오류가 발생했습니다.')
		}
	}

	const renderStatusBadge = (status: TemplateStatus) => {
		if (status === 'Y') {
			return <StatusBadge $type="active">사용</StatusBadge>
		}
		if (status === 'I') {
			return <StatusBadge $type="inactive">일시중지</StatusBadge>
		}
		return <StatusBadge $type="rejected">반려</StatusBadge>
	}

	const formatDate = (dateStr: string) => {
		if (!dateStr) return '-'
		try {
			// IWINV는 보통 yyyy-MM-dd HH:mm:ss 형태를 사용
			const d = new Date(dateStr.replace(' ', 'T'))
			if (Number.isNaN(d.getTime())) return dateStr
			return d.toLocaleString('ko-KR')
		} catch {
			return dateStr
		}
	}

	return (
		<Wrapper>
			<Header>
				<div>
					<Title>알림톡 템플릿 목록</Title>
					<Description>
						IWINV에 등록된 알림톡 템플릿을 조회합니다. 템플릿의 수정/등록은 IWINV 콘솔에서 처리한
						후 이 화면에서 상태를 확인할 수 있습니다.
					</Description>
				</div>
			</Header>

			<Filters>
				<Select
					value={filters.templateStatus}
					onChange={e =>
						handleFilterChange('templateStatus', e.target.value as TemplateStatus | '')
					}
				>
					<option value="">전체 상태</option>
					<option value="Y">사용중</option>
					<option value="I">일시중지</option>
					<option value="R">반려</option>
				</Select>
				<Input
					type="text"
					placeholder="템플릿 코드"
					value={filters.templateCode}
					onChange={e => handleFilterChange('templateCode', e.target.value)}
				/>
				<Input
					type="text"
					placeholder="템플릿명"
					value={filters.templateName}
					onChange={e => handleFilterChange('templateName', e.target.value)}
				/>
				<Buttons>
					<Button type="button" onClick={fetchTemplates} disabled={loading}>
						{loading ? '조회 중...' : '검색'}
					</Button>
					<Button type="button" onClick={handleResetFilters} disabled={loading}>
						초기화
					</Button>
				</Buttons>
			</Filters>

			<TableWrapper>
				<Table>
					<thead>
						<tr>
							<th style={{ minWidth: 130 }}>템플릿코드</th>
							<th style={{ minWidth: 160 }}>템플릿명</th>
							<th style={{ minWidth: 90 }}>상태</th>
							<th style={{ minWidth: 180 }}>생성일</th>
							<th>내용</th>
							<th style={{ minWidth: 160 }}>검수 메시지</th>
							<th style={{ minWidth: 160 }}>검수 코멘트</th>
							<th style={{ minWidth: 180 }}>기본 템플릿 설정</th>
						</tr>
					</thead>
					<tbody>
						{loading ? (
							<tr>
								<td colSpan={8} style={{ textAlign: 'center', padding: 24 }}>
									조회 중입니다...
								</td>
							</tr>
						) : templates.length === 0 ? (
							<tr>
								<td colSpan={8} style={{ textAlign: 'center', padding: 24 }}>
									조회된 템플릿이 없습니다.
								</td>
							</tr>
						) : (
							templates.map(t => (
								<tr key={t.templateCode}>
									<td>{t.templateCode}</td>
									<td>{t.templateName}</td>
									<td>{renderStatusBadge(t.status)}</td>
									<td>{formatDate(t.createDate)}</td>
									<td>
										<ContentCell title={t.templateContent}>
											{t.templateContent}
										</ContentCell>
									</td>
									<td>{t.templateStatusMsg}</td>
									<td>
										<CommentCell title={t.templateStatusComments || ''}>
											{t.templateStatusComments || '-'}
										</CommentCell>
									</td>
									<td>
										<DefaultTemplateActions>
											<small style={{ marginRight: 8 }}>기본 설정:</small>
											<DefaultSelect
												defaultValue=""
												onChange={e => {
													const value = e.target.value as
														| 'absence'
														| 'report'
														| 'attendance'
														| ''
													if (!value) return
													handleSetDefaultTemplate(t, value)
													e.target.value = ''
												}}
											>
												<option value="">선택</option>
												<option value="absence">결시</option>
												<option value="report">일지발송</option>
												<option value="attendance">등원</option>
											</DefaultSelect>
										</DefaultTemplateActions>
									</td>
								</tr>
							))
						)}
					</tbody>
				</Table>
			</TableWrapper>

			{pagination.maxPage > 1 && (
				<Pagination>
					<PageButton
						type="button"
						onClick={() => handlePageChange(filters.pageNum - 1)}
						disabled={filters.pageNum === 1}
					>
						이전
					</PageButton>
					{Array.from({ length: Math.min(5, pagination.maxPage) }, (_, idx) => {
						const start = Math.max(1, filters.pageNum - 2)
						const page = start + idx
						if (page > pagination.maxPage) return null
						return (
							<PageButton
								type="button"
								key={page}
								$data-active={page === filters.pageNum}
								onClick={() => handlePageChange(page)}
							>
								{page}
							</PageButton>
						)
					})}
					<PageButton
						type="button"
						onClick={() => handlePageChange(filters.pageNum + 1)}
						disabled={filters.pageNum === pagination.maxPage}
					>
						다음
					</PageButton>
				</Pagination>
			)}
		</Wrapper>
	)
}

export default React.memo(KakaoTemplateCatalog)

const Wrapper = styled.div`
	margin-top: 32px;
	padding: 20px;
	border-top: 2px solid #e8e8e8;
	background-color: #fafafa;
`

const Header = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	margin-bottom: 16px;
`

const Title = styled.h3`
	margin: 0;
	font-size: 18px;
	font-weight: 600;
	color: #333;
`

const Description = styled.p`
	margin: 4px 0 0;
	font-size: 13px;
	color: #666;
`

const Filters = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	margin-bottom: 16px;
`

const Input = styled.input`
	padding: 6px 10px;
	border-radius: 4px;
	border: 1px solid #d9d9d9;
	font-size: 13px;
	min-width: 140px;
`

const Select = styled.select`
	padding: 6px 10px;
	border-radius: 4px;
	border: 1px solid #d9d9d9;
	font-size: 13px;
	min-width: 120px;
	background-color: #fff;
`

const Buttons = styled.div`
	display: flex;
	gap: 8px;
`

const Button = styled.button`
	padding: 6px 12px;
	border-radius: 4px;
	border: 1px solid #d9d9d9;
	background-color: #fff;
	font-size: 13px;
	cursor: pointer;
	transition: all 0.2s;

	&:hover:not(:disabled) {
		background-color: #f5f5f5;
		border-color: #40a9ff;
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`

const TableWrapper = styled.div`
	width: 100%;
	overflow-x: auto;
`

const Table = styled.table`
	width: 100%;
	border-collapse: collapse;
	background-color: #fff;
	border: 1px solid #f0f0f0;

	th {
		padding: 10px;
		background-color: #fff5f5;
		border: 1px solid #f6f1f1;
		text-align: left;
		font-weight: 600;
		font-size: 13px;
		color: #333;
		white-space: nowrap;
	}

	td {
		padding: 10px;
		border: 1px solid #f6f1f1;
		font-size: 13px;
		color: #111;
		vertical-align: top;
	}

	tr:hover td {
		background-color: #fafafa;
	}
`

const StatusBadge = styled.span<{ $type: 'active' | 'inactive' | 'rejected' }>`
	display: inline-block;
	padding: 2px 8px;
	border-radius: 999px;
	font-size: 12px;
	font-weight: 500;
	color: ${({ $type }) =>
		$type === 'active' ? '#52c41a' : $type === 'inactive' ? '#faad14' : '#ff4d4f'};
	background-color: ${({ $type }) =>
		$type === 'active' ? '#f6ffed' : $type === 'inactive' ? '#fff7e6' : '#fff1f0'};
`

const ContentCell = styled.div`
	max-height: 3.6em;
	overflow: hidden;
	text-overflow: ellipsis;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	white-space: pre-wrap;
`

const CommentCell = styled.div`
	max-height: 3.6em;
	overflow: hidden;
	text-overflow: ellipsis;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	white-space: pre-wrap;
`

const DefaultTemplateActions = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 4px;
`

const DefaultSelect = styled.select`
	padding: 4px 8px;
	border-radius: 4px;
	border: 1px solid #d9d9d9;
	font-size: 12px;
	background-color: #fff;
`

const Pagination = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 8px;
	margin-top: 16px;
`

const PageButton = styled.button<{ 'data-active'?: boolean }>`
	padding: 6px 10px;
	min-width: 36px;
	border-radius: 4px;
	border: 1px solid ${({ 'data-active': active }) => (active ? '#F78B85' : '#d9d9d9')};
	background-color: ${({ 'data-active': active }) => (active ? '#F78B85' : '#fff')};
	color: ${({ 'data-active': active }) => (active ? '#fff' : '#111')};
	font-size: 13px;
	cursor: pointer;
	transition: all 0.2s;

	&:hover:not(:disabled) {
		background-color: ${({ 'data-active': active }) => (active ? '#f59590' : '#f5f5f5')};
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`

